/**
 * 前端模板适配器
 * 处理React相关的模板配置转换和适配
 */

import React from 'react';
import { TemplateManager, TemplateConfig, FrontendTemplateConfig, FIELD_NAMES } from '@data-management/shared';

// 前端专用的Template接口（兼容现有代码）
export interface Template {
  id: string;
  name: string;
  icon: React.ReactNode;
  description: string;
}

/**
 * 前端模板适配器类
 */
export class FrontendTemplateAdapter {
  /**
   * 将TemplateConfig转换为前端Template格式（兼容现有代码）
   */
  static convertToFrontendTemplate(config: TemplateConfig): Template {
    return {
      id: config.id,
      name: config.name,
      icon: this.createIconElement(config),
      description: config.description
    };
  }

  /**
   * 获取所有模板的前端格式
   */
  static getAllFrontendTemplates(): Template[] {
    return TemplateManager.getAllTemplates().map((config: TemplateConfig) => 
      this.convertToFrontendTemplate(config)
    );
  }

  /**
   * 创建React图标元素
   */
  private static createIconElement(config: TemplateConfig): React.ReactNode {
    const uiConfig = config.ui;
    if (!uiConfig) {
      // 默认图标样式
      return React.createElement('div', {
        style: {
          fontSize: '20px',
          color: '#1890ff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '32px',
          height: '32px',
          backgroundColor: '#f0f8ff',
          borderRadius: '6px'
        }
      }, '📄');
    }

    return React.createElement('div', {
      style: {
        fontSize: '20px',
        color: uiConfig.color || '#1890ff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '32px',
        height: '32px',
        backgroundColor: uiConfig.backgroundColor || '#f0f8ff',
        borderRadius: '6px'
      }
    }, uiConfig.icon || '📄');
  }

  /**
   * 获取表单字段配置（替换原来的getDataFields逻辑）
   */
  static getFormFieldConfig(template: Template | null): {
    titleDefaultValue?: string;
    titleDisabled: boolean;
    customerNameDefaultValue?: string;
    customerNameDisabled: boolean;
    customerQuestionDefaultValue?: string;
    customerQuestionDisabled: boolean;
    cardCountDisabled: boolean;
  } {
    if (!template) {
      return {
        titleDisabled: false,
        customerNameDisabled: false,
        customerQuestionDisabled: false,
        cardCountDisabled: false
      };
    }

    const config = TemplateManager.getConfig(template.id);
    const defaultValues = config?.fieldDefaults || {};

    return {
      titleDefaultValue: defaultValues.title,
      titleDisabled: TemplateManager.isFieldDisabled(template.id, FIELD_NAMES.TITLE),
      customerNameDefaultValue: defaultValues.customerName,
      customerNameDisabled: TemplateManager.isFieldDisabled(template.id, FIELD_NAMES.CUSTOMER_NAME),
      customerQuestionDefaultValue: defaultValues.customerQuestion,
      customerQuestionDisabled: TemplateManager.isFieldDisabled(template.id, FIELD_NAMES.CUSTOMER_QUESTION),
      cardCountDisabled: TemplateManager.isFieldDisabled(template.id, FIELD_NAMES.CARD_COUNT)
    };
  }

  /**
   * 获取模板默认值（用于表单预填充）
   */
  static getTemplateDefaultValues(template: Template): Record<string, any> {
    return TemplateManager.getDefaultValues(template.id);
  }

  /**
   * 检查模板是否支持指定生成模式
   */
  static supportsGenerateMode(templateId: string, mode: 'normal' | 'thinking'): boolean {
    const options = TemplateManager.getGenerateOptions(templateId);
    if (!options) return true; // 默认支持所有模式
    
    return options[mode];
  }

  /**
   * 获取模板的卡片信息配置
   */
  static getCardInfos(templateId: string): string[] | undefined {
    const defaultValues = TemplateManager.getDefaultValues(templateId);
    return defaultValues.cardInfos;
  }
}

// 便捷导出
export const templateAdapter = FrontendTemplateAdapter;
